// Leaving and coming in over a screen edge, for hosts with one runtime per screen (the desktop
// app's monitors). Each goes its own way: off the side it walks, rolls, hops, waddles or slides
// along the floor (a flier flies, a shulker teleports), off the top it flies up (the elytra or
// the jetpack), and off the bottom it hops down. It comes in the same way: along the floor from
// the side, from above it falls and opens its parachute, from below it jumps up onto the floor.
import { env, clamp, rand } from '../engine/core.js';
import { FLOOR, floorY } from '../engine/world.js';
import { travelTo, walkOn, fallDown, hopTo, arrive, WAYS, generalWay } from '../engine/travel.js';
import { can, walkStyle, ownTravel } from '../engine/catalog.js';
import { ability } from '../engine/abilities.js';

export const SIDES = new Set(['left', 'right', 'top', 'bottom']);

const tallOf = me => (Math.max(me.shapes.normal.bh, me.shapes.side.bh) + me.shapes.normal.leg) * me.s;
// How far past the edge its feet must be for all of it to be just out of sight (its widest
// drawing and its arms), so it crosses from one screen to the next without a long gap.
const outside = me => (Math.max(me.shapes.normal.bw, me.shapes.side.bw) / 2 + 2) * me.s;
// A spot just past an edge, for the ways of travel that go to a spot.
const past = (x, y) => ({ ledge: { id: 'edge:past', kind: 'edge', ownerId: null, weight: 0, geom: () => ({ x1: x - 1, x2: x + 1, y }) }, dx: 1, rest: 'stand' });
// Its own way of travelling, if it has one it may use (a flier's flight, a shulker's teleport).
const ownWay = (me, ok = () => true) => ownTravel(me.design).find(w => can(me, w) && ability(w)?.travel && ok(ability(w)));

function floorSpot(me, x) {
  const f = FLOOR.geom(), s = me.s;
  const lo = f.x1 + 10 * s, hi = Math.max(lo, f.x2 - 10 * s);
  return { ledge: FLOOR, dx: clamp(x, lo, hi) - f.x1, rest: 'stand' };
}

function moveFree(me) {
  me.anchor = null; me.moving = true; me.dest = null;
}

// Along the floor to x (on or off screen), its own way.
async function along(g, x) {
  const me = g.me;
  if (walkStyle(me.design) !== 'none') return walkOn(FLOOR, x, g, { stayTurned: true });
  const own = ownWay(me);
  if (own) await ability(own).travel(past(x, floorY()), g);
  else me.x = x;
  return true;
}

// It goes out of sight over `side`. Resolves once it is gone; throws Cancelled when interrupted.
export async function leaveOver(g, side) {
  const me = g.me, s = me.s, f = FLOOR.geom();
  if (side === 'left' || side === 'right') {
    const dir = side === 'right' ? 1 : -1;
    // First down to the floor near that end, its usual way.
    if (me.anchor?.ledge !== FLOOR) await travelTo(floorSpot(me, dir > 0 ? f.x2 - 40 * s : f.x1 + 40 * s), g);
    moveFree(me);
    await along(g, dir > 0 ? env.W + outside(me) : -outside(me));
  } else if (side === 'top') {
    const spot = past(clamp(me.x, 0, env.W), -tallOf(me) - 6 * s);
    const up = generalWay(me, 'up', way => can(me, way) && (way === 'elytra' || way === 'jetpack'));
    const own = ownWay(me, a => !a.down);
    if (up) await WAYS[up](spot, g);
    else if (own) await ability(own).travel(spot, g);
    else await hopTo(() => ({ x: spot.ledge.geom().x1 + 1, y: spot.ledge.geom().y }), g);
  } else {
    if (me.anchor?.ledge !== FLOOR) await travelTo(floorSpot(me, me.x), g);
    moveFree(me);
    const x = me.x + (me.x < env.W / 2 ? 1 : -1) * 12 * s;
    await hopTo(() => ({ x, y: env.H + tallOf(me) + 4 * s }), g);
  }
  me.alpha = 0;
}

// Just past `side` (`at` 0..1 along the top or bottom), where it comes in from.
export function placeOutside(me, side, at = 0.5) {
  const s = me.s, x = clamp(at, 0, 1) * env.W;
  if (side === 'left' || side === 'right') { me.x = side === 'left' ? -outside(me) : env.W + outside(me); me.y = floorY(); }
  else if (side === 'top') { me.x = clamp(x, 12 * s, env.W - 12 * s); me.y = -4 * s; }
  else { me.x = clamp(x, 12 * s, env.W - 12 * s); me.y = env.H + tallOf(me) + 4 * s; }
  moveFree(me);
  me.alpha = 1;
}

// In over `side` from where placeOutside put it, onto the floor (or, from above, whatever it lands on).
export async function comeIn(g, side) {
  const me = g.me, s = me.s, f = FLOOR.geom();
  moveFree(me);
  if (side === 'top') { await fallDown(g); return; }
  let spot;
  if (side === 'bottom') {
    spot = floorSpot(me, me.x);
    await hopTo(() => ({ x: f.x1 + spot.dx, y: floorY() }), g);
  } else {
    const from = side === 'left' ? f.x1 : f.x2, dir = side === 'left' ? 1 : -1;
    spot = floorSpot(me, from + dir * rand(30, 80) * s);
    await along(g, f.x1 + spot.dx);
  }
  arrive(me, spot);
}
